<template>
    <div>
        <BaseModal
            :isVisible="modalStatus"
            :title="title"
            size="xl"
            @close="clean"
        >
            <template v-slot:content>
                <div class="user-details">
                    <SectionContainer
                        title="Dados do Usuário"
                        icon="user"
                    >
                        <!-- Header com foto e informações principais (usando classes do modal de moderação) -->
                        <div class="reservation-header">
                            <div class="vehicle-section text-center">
                                <img
                                    v-if="userData && userData.photo"
                                    :src="userData.photo"
                                    :alt="`Foto de ${userData.name}`"
                                    class="vehicle-image"
                                    @error="handleImageError"
                                    style="border-radius: 50%; height: 200px; width: 200px"
                                />
                            </div>

                            <div class="status-section">
                                <div class="status-info">
                                    <span class="status-badge status-approved">
                                        Dados do Usuário
                                    </span>

                                    <div
                                        class="reservation-id"
                                        v-if="userData && userData.id"
                                    >
                                        #{{ userData.id }}
                                    </div>
                                </div>

                                <div
                                    class="user-info"
                                    v-if="userData && userData.cellphone"
                                >
                                    <i class="fas fa-mobile-alt text-primary"></i>
                                    <span>{{ userData.cellphone }}</span>
                                </div>

                                <div
                                    class="user-info"
                                    v-if="userData && userData.number_cnh"
                                >
                                    <i class="fas fa-id-card text-success"></i>
                                    <span>CNH: {{ userData.number_cnh }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Grid de informações em cards (usando classes do modal de moderação) -->
                        <div
                            class="info-cards-grid"
                            v-if="userData"
                        >
                            <div class="info-card">
                                <div class="card-icon">
                                    <i class="fas fa-user"></i>
                                </div>
                                <div class="card-content">
                                    <div class="card-label">Nome do Usuário:</div>
                                    <div class="card-value">{{ userData.name || 'Não informado' }}</div>
                                </div>
                            </div>

                            <div class="info-card">
                                <div class="card-icon">
                                    <i class="fas fa-envelope"></i>
                                </div>
                                <div class="card-content">
                                    <div class="card-label">Email do Usuário:</div>
                                    <div class="card-value">{{ userData.email || 'Não informado' }}</div>
                                </div>
                            </div>

                            <div class="info-card">
                                <div class="card-icon">
                                    <i class="fas fa-calendar-alt"></i>
                                </div>
                                <div class="card-content">
                                    <div class="card-label">Data de Validade da CNH:</div>
                                    <div class="card-value orange">
                                        {{ formatDate(userData.validity_cnh) || 'Não informado' }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SectionContainer>
                </div>
            </template>
        </BaseModal>

        <Loading
            :active.sync="isLoading"
            :is-full-page="fullPage"
        />
    </div>
</template>

<script>
import BaseModal from '../Base/BaseModal.vue'
import SectionContainer from './Checklist/SectionContainer.vue'
import Loading from 'vue-loading-overlay'
import {getMessage} from "../../composables/message"

export default {
    name: "UserModal",
    components: {
        BaseModal,
        SectionContainer,
        Loading
    },
    emits: ['changeModal', 'user-updated'],
    props: {
        modalStatus: {
            type: Boolean,
            required: true
        },
        dataModal: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            default: 'Visualizar Usuário'
        }
    },
    data() {
        return {
            userData: {},
            isLoading: false,
            fullPage: true,
            imageError: false
        }
    },
    watch: {
        modalStatus: function (newStatus) {
            if (newStatus) {
                this.loadUserData()
            }
        },
        dataModal: {
            handler: function (newData) {
                this.loadUserData()
            },
            deep: true
        }
    },
    mounted() {
        this.loadUserData()
    },
    methods: {
        loadUserData() {
            if (this.dataModal && this.dataModal.user) {
                this.userData = {...this.dataModal.user}
            } else {
                this.userData = {}
            }
        },
        formatDate(dateString) {
            if (!dateString) return null

            const date = new Date(dateString)
            if (isNaN(date.getTime())) return dateString

            return date.toLocaleDateString('pt-BR')
        },
        handleImageError() {
            this.imageError = true
        },
        sendLateNotification() {
            if (!this.userData.id) {
                this.$message(null, 'Usuário não encontrado', 'error')
                return
            }

            this.isLoading = true
            const api = this.$store.state.api + "notifications/late"
            const formData = new FormData()

            formData.append("user_id", this.userData.id)
            if (this.dataModal.devolution_date) {
                formData.append("devolution_date", this.dataModal.devolution_date)
            }

            this.$http
                .post(api, formData)
                .then(response => {
                    this.$message(
                        "Sucesso",
                        "Notificação enviada com sucesso!",
                        "success"
                    )
                    this.$emit('user-updated', this.userData)
                })
                .catch(error => {
                    const message = error.response.data.errors
                        ? getMessage(error.response.data.errors)
                        : error.response.data
                    this.$message(null, message, "error")
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        clean() {
            this.$emit("changeModal", false)
            this.imageError = false
        }
    }
}
</script>

<style scoped>
/* Mantendo apenas o estilo específico para o placeholder e foto redonda */
.no-photo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--text-color, #6c757d);
    opacity: 0.6;
    background-color: var(--light-gray, #f8f9fa);
}

.no-photo-placeholder i {
    font-size: 48px;
    margin-bottom: 8px;
}

.no-photo-placeholder span {
    font-size: 12px;
}

/*
  Todas as outras classes são reutilizadas do arquivo moderation-reserve.css:
  - .reservation-header
  - .vehicle-section
  - .vehicle-image-container
  - .vehicle-image
  - .vehicle-badge
  - .plate-container
  - .status-section
  - .status-info
  - .status-badge
  - .reservation-id
  - .user-info
  - .info-cards-grid
  - .info-card
  - .card-icon
  - .card-content
  - .card-label
  - .card-value
  - .observation-section
  - .action-buttons
*/
</style>



// WEBPACK FOOTER //
// src/components/Modals/UserModal.vue